import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { loadOCRConfig, saveOCRConfig, getAvailableProviders } from '../../../services/ocr/OCRService';

const ACCENT = '#34D399';
const BLUE = '#60A5FA';
const AMBER = '#FBBF24';
const RED = '#F87171';
const PURPLE = '#A78BFA';

const inputCls = 'w-full px-3 py-2.5 rounded-xl text-sm font-oranienbaum text-white/85 outline-none transition-colors';
const inputSt = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
const inputFocusSt = { background: 'rgba(255,255,255,0.07)', border: `1px solid ${BLUE}40` };

/**
 * OCR Configuration Panel
 * Shown inside Configuración → IA → OCR
 */
const OCRConfigPanel = () => {
  const [config, setConfig] = useState({
    provider: 'ocr_space',
    api_key: '',
    timeout: 60000,
    language: 'spa',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const providers = getAvailableProviders();
  const activeProvider = providers?.find(p => p?.id === config?.provider);

  useEffect(() => {
    loadOCRConfig()?.then(cfg => { setConfig(cfg); setLoading(false); })?.catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    setSaved(false);
    try {
      await saveOCRConfig(config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.message || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const getInputStyle = (field) => ({
    ...inputSt,
    ...(focusedField === field ? inputFocusSt : {}),
  });

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6">
        <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${BLUE}40`, borderTopColor: BLUE }} />
        <span className="font-oranienbaum text-white/40 text-sm">Cargando configuración OCR...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${BLUE}12`, border: `1px solid ${BLUE}25` }}>
          <Icon name="ScanLine" size={18} style={{ color: BLUE }} />
        </div>
        <div>
          <h3 className="font-oranienbaum font-bold text-white text-base">OCR — Reconocimiento de Facturas</h3>
          <p className="font-oranienbaum text-white/40 text-xs">
            Configura el proveedor que extrae texto de tus facturas (PDF e imágenes)
          </p>
        </div>
      </div>

      {/* Flow diagram */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { icon: 'FileText', label: 'Factura', color: 'rgba(255,255,255,0.30)' },
          { icon: 'ArrowRight', label: null, color: 'rgba(255,255,255,0.15)' },
          { icon: 'ScanLine', label: 'OCR.Space', color: BLUE },
          { icon: 'ArrowRight', label: null, color: 'rgba(255,255,255,0.15)' },
          { icon: 'FileText', label: 'Texto', color: ACCENT },
          { icon: 'ArrowRight', label: null, color: 'rgba(255,255,255,0.15)' },
          { icon: 'Brain', label: 'IA interpreta', color: PURPLE },
          { icon: 'ArrowRight', label: null, color: 'rgba(255,255,255,0.15)' },
          { icon: 'CheckCircle2', label: 'Revisión', color: AMBER },
        ]?.map((item, i) => (
          item?.label === null
            ? <Icon key={i} name="ArrowRight" size={12} style={{ color: item?.color }} />
            : (
              <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg"
                style={{ background: `${item?.color}10`, border: `1px solid ${item?.color}20` }}>
                <Icon name={item?.icon} size={11} style={{ color: item?.color }} />
                <span className="font-oranienbaum text-[10px]" style={{ color: item?.color }}>{item?.label}</span>
              </div>
            )
        ))}
      </div>

      {/* Provider selection */}
      <div>
        <label className="block font-oranienbaum text-white/50 text-xs mb-2 uppercase tracking-wider">
          Proveedor OCR
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {providers?.map(p => (
            <button
              key={p?.id}
              disabled={p?.comingSoon}
              onClick={() => !p?.comingSoon && setConfig(prev => ({ ...prev, provider: p?.id }))}
              className="text-left p-3 rounded-xl transition-all"
              style={
                config?.provider === p?.id
                  ? { background: `${BLUE}15`, border: `1px solid ${BLUE}40` }
                  : p?.comingSoon
                    ? { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', opacity: 0.5 }
                    : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }
              }>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full border-2 flex items-center justify-center"
                    style={{
                      borderColor: config?.provider === p?.id ? BLUE : 'rgba(255,255,255,0.25)',
                      background: config?.provider === p?.id ? BLUE : 'transparent',
                    }}>
                    {config?.provider === p?.id && <div className="w-1 h-1 rounded-full bg-white" />}
                  </div>
                  <span className="font-oranienbaum font-semibold text-white/85 text-sm">{p?.label}</span>
                </div>
                {p?.comingSoon && (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] font-oranienbaum uppercase tracking-wider"
                    style={{ background: `${AMBER}12`, color: AMBER, border: `1px solid ${AMBER}25` }}>
                    Próximamente
                  </span>
                )}
                {config?.provider === p?.id && !p?.comingSoon && (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] font-oranienbaum uppercase tracking-wider"
                    style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
                    Activo
                  </span>
                )}
              </div>
              <p className="font-oranienbaum text-white/35 text-[11px] leading-relaxed ml-5">{p?.description}</p>
              {p?.docsUrl && !p?.comingSoon && (
                <a href={p?.docsUrl} target="_blank" rel="noopener noreferrer"
                  onClick={e => e?.stopPropagation()}
                  className="ml-5 mt-1 inline-flex items-center gap-1 font-oranienbaum text-[10px] hover:underline"
                  style={{ color: BLUE }}>
                  <Icon name="ExternalLink" size={9} />
                  Documentación
                </a>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* API Key */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="font-oranienbaum text-white/50 text-xs uppercase tracking-wider">
            {activeProvider?.apiKeyLabel || 'API Key'}
          </label>
          {config?.provider === 'ocr_space' && (
            <a href="https://ocr.space/ocrapi/freekey" target="_blank" rel="noopener noreferrer"
              className="font-oranienbaum text-[10px] flex items-center gap-1 hover:underline"
              style={{ color: ACCENT }}>
              <Icon name="Key" size={9} />
              Obtener API Key gratuita
            </a>
          )}
        </div>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={config?.api_key || ''}
            onChange={e => setConfig(prev => ({ ...prev, api_key: e?.target?.value }))}
            onFocus={() => setFocusedField('api_key')}
            onBlur={() => setFocusedField(null)}
            placeholder={activeProvider?.apiKeyPlaceholder || 'Introduce tu API Key'}
            className={`${inputCls} pr-10`}
            style={getInputStyle('api_key')}
          />
          <button
            type="button"
            onClick={() => setShowKey(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
            <Icon name={showKey ? 'EyeOff' : 'Eye'} size={14} />
          </button>
        </div>
        {config?.provider === 'ocr_space' && !config?.api_key && (
          <p className="mt-1.5 font-oranienbaum text-[11px]" style={{ color: AMBER }}>
            ⚠ Sin API Key el OCR no funcionará. Usa "helloworld" para pruebas (límite bajo) u obtén una clave gratuita.
          </p>
        )}
        {config?.api_key && (
          <p className="mt-1.5 font-oranienbaum text-[11px]" style={{ color: ACCENT }}>
            ✓ API Key configurada
          </p>
        )}
      </div>

      {/* Advanced settings */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block font-oranienbaum text-white/50 text-xs mb-1.5 uppercase tracking-wider">
            Idioma principal
          </label>
          <select
            value={config?.language || 'spa'}
            onChange={e => setConfig(prev => ({ ...prev, language: e?.target?.value }))}
            className={inputCls}
            style={{ ...inputSt, background: '#1A1A1A', color: 'rgba(255,255,255,0.85)' }}>
            <option value="spa">Español</option>
            <option value="eng">Inglés</option>
            <option value="fra">Francés</option>
            <option value="por">Portugués</option>
          </select>
        </div>
        <div>
          <label className="block font-oranienbaum text-white/50 text-xs mb-1.5 uppercase tracking-wider">
            Timeout (segundos)
          </label>
          <input
            type="number"
            min={10}
            max={300}
            value={Math.round((config?.timeout || 60000) / 1000)}
            onChange={e => setConfig(prev => ({ ...prev, timeout: parseInt(e?.target?.value || '60', 10) * 1000 }))}
            onFocus={() => setFocusedField('timeout')}
            onBlur={() => setFocusedField(null)}
            className={inputCls}
            style={getInputStyle('timeout')}
          />
        </div>
      </div>

      {/* Info box */}
      <div className="p-3 rounded-xl" style={{ background: `${BLUE}06`, border: `1px solid ${BLUE}15` }}>
        <div className="flex items-start gap-2">
          <Icon name="Info" size={13} style={{ color: BLUE, marginTop: 1 }} />
          <div className="space-y-1">
            <p className="font-oranienbaum text-white/50 text-[11px] leading-relaxed">
              <strong className="text-white/70">Flujo:</strong> El documento se sube a Supabase Storage → OCR.Space extrae el texto → la IA del ERP interpreta la factura → tú revisas y confirmas.
            </p>
            <p className="font-oranienbaum text-white/40 text-[11px]">
              El OCR nunca interpreta productos. Solo extrae texto. La IA es quien estructura los datos.
            </p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: `${RED}10`, border: `1px solid ${RED}25` }}>
          <Icon name="AlertCircle" size={14} style={{ color: RED }} />
          <p className="font-oranienbaum text-sm" style={{ color: RED }}>{error}</p>
        </div>
      )}

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-oranienbaum font-semibold text-sm transition-all"
          style={
            saved
              ? { background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }
              : { background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }
          }>
          {saving ? (
            <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: `${BLUE}40`, borderTopColor: BLUE }} />
          ) : saved ? (
            <Icon name="CheckCircle2" size={15} />
          ) : (
            <Icon name="Save" size={15} />
          )}
          {saving ? 'Guardando...' : saved ? '¡Guardado!' : 'Guardar configuración OCR'}
        </button>
        {saved && (
          <span className="font-oranienbaum text-xs" style={{ color: ACCENT }}>
            Configuración guardada correctamente
          </span>
        )}
      </div>
    </div>
  );
};

export default OCRConfigPanel;
